import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ThumbUpOutlinedIcon from "@material-ui/icons/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@material-ui/icons/ThumbDownOutlined";
import SendIcon from "@material-ui/icons/Send";
import CheckIcon from "@material-ui/icons/Check";
import MarkdownRenderer from "./MarkdownRenderer";
import EvaluationPanel from "./EvaluationPanel";
import useThemeColors from "../../hooks/useThemeColors";
import { submitChatRating } from "../../services/chatApi";

const useStyles = makeStyles(() => ({
  "@keyframes fadeInUp": {
    from: {
      opacity: 0,
      transform: "translateY(8px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },
  messageRow: {
    display: "flex",
    marginBottom: "16px",
    animation: "$fadeInUp 0.3s ease-out both",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  messageRowUser: {
    justifyContent: "flex-end",
  },
  messageRowAssistant: {
    justifyContent: "flex-start",
  },
  bubble: {
    maxWidth: "85%",
    borderRadius: "12px",
    padding: "10px 14px",
    fontSize: "14px",
    lineHeight: "1.6",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
  },
  bubbleUser: {
    color: "#fff",
    borderTopRightRadius: "4px",
  },
  bubbleError: {
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    borderTopLeftRadius: "4px",
  },
  bubbleAssistant: {
    backgroundColor: "#f5f5f5",
    color: "#333",
    borderTopLeftRadius: "4px",
    "& p": {
      margin: "0 0 8px 0",
      "&:last-child": { margin: 0 },
    },
    "& h1": {
      fontSize: "15px",
      fontWeight: 600,
      margin: "16px 0 8px 0",
      "&:first-child": { marginTop: 0 },
    },
    "& h2": {
      fontSize: "15px",
      fontWeight: 600,
      margin: "16px 0 8px 0",
      "&:first-child": { marginTop: 0 },
    },
    "& h3": {
      fontSize: "14px",
      fontWeight: 600,
      margin: "12px 0 6px 0",
    },
    "& h4": {
      fontSize: "13.5px",
      fontWeight: 600,
      margin: "10px 0 4px 0",
    },
    "& ul, & ol": {
      margin: "4px 0 8px 0",
      paddingLeft: "20px",
    },
    "& li": {
      marginBottom: "4px",
    },
    "& blockquote": {
      margin: "8px 0",
      padding: "8px 12px",
      borderLeft: "3px solid #ccc",
      backgroundColor: "#fafafa",
      borderRadius: "0 4px 4px 0",
      "& p": { margin: 0 },
    },
    "& code": {
      fontFamily: '"Roboto Mono", "Consolas", monospace',
      fontSize: "12.5px",
      backgroundColor: "#e8e8e8",
      padding: "1px 4px",
      borderRadius: "3px",
    },
    "& pre": {
      margin: "8px 0",
      backgroundColor: "#1e1e1e",
      borderRadius: "6px",
      padding: "12px",
      overflow: "auto",
      "& code": {
        backgroundColor: "transparent",
        padding: 0,
        color: "#d4d4d4",
        fontSize: "12.5px",
      },
    },
    "& hr": {
      border: "none",
      borderTop: "1px solid #ddd",
      margin: "12px 0",
    },
    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      margin: "8px 0",
      fontSize: "12.5px",
    },
    "& th": {
      backgroundColor: "#eee",
      fontWeight: 600,
      textAlign: "left",
      padding: "6px 10px",
      borderBottom: "2px solid #ccc",
      whiteSpace: "nowrap",
    },
    "& td": {
      padding: "5px 10px",
      borderBottom: "1px solid #e8e8e8",
    },
    "& tr:hover td": {
      backgroundColor: "#f9f9f9",
    },
  },
  artifactBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "8px",
    padding: "4px 10px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "12px",
    color: "#555",
    fontWeight: 500,
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "#999",
      backgroundColor: "#f5f5f5",
    },
  },
  artifactIcon: {
    fontSize: "14px !important",
  },
  ratingRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginTop: "6px",
    paddingTop: "4px",
  },
  ratingBtn: {
    background: "none",
    border: "none",
    padding: "3px 5px",
    cursor: "pointer",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.06)",
    },
  },
  ratingIcon: {
    fontSize: "15px !important",
  },
  "@keyframes chipFadeIn": {
    from: { opacity: 0, transform: "translateY(4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  categoryRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",
    marginTop: "4px",
    animation: "$chipFadeIn 0.2s ease-out both",
  },
  categoryChip: {
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "10px",
    border: "1px solid #ddd",
    background: "none",
    color: "#888",
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontFamily: "inherit",
    lineHeight: 1.6,
    "&:hover": {
      borderColor: "#aaa",
      color: "#555",
    },
  },
  categoryChipActive: {
    borderColor: "#90a4ae",
    backgroundColor: "#eceff1",
    color: "#455a64",
  },
  categorySendBtn: {
    background: "none",
    border: "none",
    padding: "2px 4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    borderRadius: "4px",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.06)",
    },
  },
  categorySendIcon: {
    fontSize: "13px !important",
    color: "#90a4ae",
  },
  categorySentIcon: {
    fontSize: "13px !important",
    color: "#66bb6a",
  },
  categorySentLabel: {
    fontSize: "11px",
    color: "#66bb6a",
    marginLeft: "2px",
    animation: "$chipFadeIn 0.2s ease-out both",
  },
}));

const ARTIFACT_ICONS = {
  table: "≡",
  formula: "ƒ",
  diagram: "⇢",
};

const BADGE_RE = /\[ARTIFACT_BADGE:(art-\d+):(\w+):([^\]]+)\]/g;

const FEEDBACK_CATEGORIES = ["language", "format", "precision", "completeness"];

export default function ChatMessage({
  message,
  messageIndex,
  messages,
  onSelectArtifact,
  isDev,
  evaluationMode,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientColor, clientTextColor } = useThemeColors();
  const [currentRating, setCurrentRating] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesSent, setCategoriesSent] = useState(false);
  const isUser = message.role === "user";
  const isError = message.isError === true;
  const isAssistant = !isUser && !isError;

  const handleRate = useCallback(
    (rating) => {
      const next = currentRating === rating ? null : rating;
      setCurrentRating(next);
      setCategoriesSent(false);
      setSelectedCategories([]);

      if (next) {
        const prevUserMsg = messages
          ? [...messages]
              .slice(0, messageIndex)
              .reverse()
              .find((m) => m.role === "user")
          : null;

        submitChatRating(
          next,
          messageIndex,
          prevUserMsg ? prevUserMsg.content.slice(0, 500) : "",
          message.content.slice(0, 500),
          messages
            ? messages.slice(0, messageIndex + 1).map((m) => ({
                role: m.role,
                content: m.content,
              }))
            : null,
          message.usageLogId || null,
        ).catch(() => {});
      }
    },
    [currentRating, message, messageIndex, messages],
  );

  const toggleCategory = useCallback((cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  }, []);

  const sendCategories = useCallback(() => {
    if (selectedCategories.length === 0) return;

    const prevUserMsg = messages
      ? [...messages]
          .slice(0, messageIndex)
          .reverse()
          .find((m) => m.role === "user")
      : null;

    submitChatRating(
      currentRating,
      messageIndex,
      prevUserMsg ? prevUserMsg.content.slice(0, 500) : "",
      message.content.slice(0, 500),
      messages
        ? messages.slice(0, messageIndex + 1).map((m) => ({
            role: m.role,
            content: m.content,
          }))
        : null,
      message.usageLogId || null,
      selectedCategories,
    ).catch(() => {});

    setCategoriesSent(true);
  }, [selectedCategories, currentRating, message, messageIndex, messages]);

  const bubbleClass = isUser
    ? classes.bubbleUser
    : isError
      ? classes.bubbleError
      : classes.bubbleAssistant;

  return (
    <div
      className={`${classes.messageRow} ${isUser ? classes.messageRowUser : classes.messageRowAssistant}`}
    >
      <div
        className={`${classes.bubble} ${bubbleClass}`}
        style={
          isUser
            ? { backgroundColor: clientColor, color: clientTextColor }
            : undefined
        }
      >
        {isUser ? (
          message.content
        ) : isError ? (
          message.content
        ) : (
          <>
            {(() => {
              const parts = [];
              let lastIdx = 0;
              const text = message.content;
              const artMap = {};
              (message.artifacts || []).forEach((a) => {
                artMap[a.id] = a;
              });

              let m;
              const regex = new RegExp(BADGE_RE.source, "g");
              while ((m = regex.exec(text)) !== null) {
                if (m.index > lastIdx) {
                  parts.push({
                    type: "text",
                    value: text.slice(lastIdx, m.index),
                  });
                }
                parts.push({
                  type: "badge",
                  artId: m[1],
                  artType: m[2],
                  artTitle: m[3],
                });
                lastIdx = regex.lastIndex;
              }
              if (lastIdx < text.length) {
                parts.push({ type: "text", value: text.slice(lastIdx) });
              }

              if (parts.length === 0) {
                return <MarkdownRenderer content={text} />;
              }
              return parts.map((part, i) => {
                if (part.type === "text") {
                  return <MarkdownRenderer key={i} content={part.value} />;
                }
                const artifact = artMap[part.artId];
                if (!artifact) return null;
                return (
                  <div
                    key={part.artId}
                    className={classes.artifactBadge}
                    onClick={() =>
                      onSelectArtifact && onSelectArtifact(artifact)
                    }
                  >
                    <span style={{ fontSize: 14 }}>
                      {ARTIFACT_ICONS[part.artType] || "📋"}
                    </span>
                    {part.artTitle}
                    <OpenInNewIcon className={classes.artifactIcon} />
                  </div>
                );
              });
            })()}

            {isAssistant && (
              <div className={classes.ratingRow}>
                <button
                  className={classes.ratingBtn}
                  onClick={() => handleRate("up")}
                  title={t("ai_chat.rating_good")}
                >
                  <ThumbUpOutlinedIcon
                    className={classes.ratingIcon}
                    style={{
                      color: currentRating === "up" ? "#4caf50" : "#bdbdbd",
                    }}
                  />
                </button>
                <button
                  className={classes.ratingBtn}
                  onClick={() => handleRate("down")}
                  title={t("ai_chat.rating_poor")}
                >
                  <ThumbDownOutlinedIcon
                    className={classes.ratingIcon}
                    style={{
                      color: currentRating === "down" ? "#ef5350" : "#bdbdbd",
                    }}
                  />
                </button>
              </div>
            )}

            {isDev && currentRating && (
              <div className={classes.categoryRow}>
                {FEEDBACK_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    className={`${classes.categoryChip} ${
                      selectedCategories.includes(cat)
                        ? classes.categoryChipActive
                        : ""
                    }`}
                    onClick={() => !categoriesSent && toggleCategory(cat)}
                    disabled={categoriesSent}
                    style={
                      categoriesSent
                        ? { opacity: 0.5, cursor: "default" }
                        : undefined
                    }
                  >
                    {t(`ai_chat.feedback_${cat}`, { defaultValue: cat })}
                  </button>
                ))}
                {!categoriesSent && selectedCategories.length > 0 && (
                  <button
                    className={classes.categorySendBtn}
                    onClick={sendCategories}
                    title={t("ai_chat.send_feedback", {
                      defaultValue: "Send",
                    })}
                  >
                    <SendIcon className={classes.categorySendIcon} />
                  </button>
                )}
                {categoriesSent && (
                  <span className={classes.categorySentLabel}>
                    <CheckIcon className={classes.categorySentIcon} />
                  </span>
                )}
              </div>
            )}

            {evaluationMode && isAssistant && message.usageLogId && (
              <EvaluationPanel usageLogId={message.usageLogId} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

ChatMessage.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    isError: PropTypes.bool,
    usageLogId: PropTypes.number,
    artifacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        content: PropTypes.string,
      }),
    ),
  }).isRequired,
  messageIndex: PropTypes.number,
  messages: PropTypes.array,
  onSelectArtifact: PropTypes.func,
  isDev: PropTypes.bool,
  evaluationMode: PropTypes.bool,
};
